import { TabulatorFull } from "tabulator-tables";

import { Factory } from "@/data/dao/Factory";
import type { DaoError, IDataSource } from "@/data/dao/service/BaseServiceDao";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { OperatorDao } from "@/data/dao/service/OperatorDao";
import type { IStock } from "@/data/model/Article";
import {
    ArticleUnavailabilityReason,
    ItemAvailability,
    ItemType,
    OperatorItemAvailabilityAndStockLevelClaimValue,
} from "@/data/model/Enums";
import type { IOffer } from "@/data/model/Offer";
import type { IOfferCategory } from "@/data/model/OfferCategory";
import type { ILinkableEntity } from "@/data/model/Shared";
import { exceptionTypes } from "@/data/util/Config";
import { Format } from "@/data/util/Format";
import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";

import { logError } from "@/shared/logging/errors";
import { toMoneyWithSign } from "@/shared/utils/format";

import { Popup } from "@/components/dialog/Popup";

import { OperatorClient } from "@/operator/OperatorClient";
import { ProductionContext } from "@/operator/context/ProductionContext";
import { SelectBootstrap } from "@/operator/elements/SelectBootstrap";
import { StockTableEditor } from "@/operator/elements/StockTableEditor";
import { Switch } from "@/operator/elements/Switch";
import { operatorEvents } from "@/operator/events/operatorEvents";
import { trans } from "@/operator/localization/Trans";
import { UserMenu } from "@/operator/ui/UserMenu";
import { FulfillmentSoundNotifications } from "@/operator/ux/SoundNotifications";

import { BaseController, ViewTransition } from "./BaseController";

class TableRow {
    id: string;
    designation: string;
    standardPrice: string;
    isAvailable: boolean;
    unavailabilityReason: string;
    locationId: string;
    stock: IStock | undefined;

    constructor(
        id: string,
        designation: string,
        standardPrice: string,
        isAvailable: boolean,
        unavailabilityReason: string,
        locationId: string,
        stock: IStock | undefined
    ) {
        this.id = id;
        this.designation = designation;
        this.standardPrice = standardPrice;
        this.isAvailable = isAvailable;
        this.unavailabilityReason = unavailabilityReason;
        this.locationId = locationId;
        this.stock = stock;
    }
}

interface ItemAvailabilityViewLinkableEntity extends ILinkableEntity {
    id: string;
    name: string;
    appearsIn?: ILinkableEntity[];
    value: ItemAvailability;
}

interface ItemTypeViewLinkableEntity extends ILinkableEntity {
    id: string;
    name: string;
    appearsIn?: ILinkableEntity[];
    value: ItemType;
}

export class ItemsController extends BaseController {
    private productionContext: ProductionContext;

    private readonly $head: JQuery;
    private readonly $filterHeader: JQuery;
    private offerSelect: SelectBootstrap<IOffer>;
    private menuCategorySelect: SelectBootstrap<IOfferCategory>;
    private itemTypeSelect: SelectBootstrap<ItemTypeViewLinkableEntity>;
    private availabilitySelect: SelectBootstrap<ItemAvailabilityViewLinkableEntity>;
    private $articleSearch: JQuery;
    private $searchButton: JQuery;

    private selectedOfferIds: string[] = [];
    private selectedMenuCategoriesIds: string[] = [];

    private selectedAvailability: string = "";
    private selectedItemType: string = "";

    private searchedArticleName: string = "";

    private catalogDao: CatalogDao;
    private operatorDao: OperatorDao;
    private data: TableRow[] = [];
    private table?: TabulatorFull;

    private availabilityDataSource: IDataSource<ItemAvailabilityViewLinkableEntity> = {
        getData: async () => {
            const items = Object.keys(ItemAvailability)
                .filter((x) => isNaN(Number(x)))
                .map(
                    (x) =>
                        ({
                            name: trans.enums.itemAvailability[x],
                            id: x,
                            value: ItemAvailability[x] as ItemAvailability,
                            appearsIn: [],
                        }) as ItemAvailabilityViewLinkableEntity
                );
            return { items: items, hasPrev: false, hasNext: false, limit: 0, offset: 0 };
        },
    };

    private menuCategoriesDataSource: IDataSource<IOfferCategory> = {
        getData: async (_, abortSignal?: AbortSignal) => {
            const result = await this.catalogDao.offerCategoryDataSource.getData(
                {
                    limit: 1000,
                },
                abortSignal
            );
            const filteredOffers =
                this.selectedOfferIds.length === 0
                    ? result.items
                    : result.items.filter((off) =>
                          off.offers.some((o) =>
                              this.selectedOfferIds.some((selectedOfferId) => selectedOfferId == o.id)
                          )
                      );
            return { items: filteredOffers, hasPrev: false, hasNext: false, limit: 0, offset: 0 };
        },
    };

    private itemTypeDataSource: IDataSource<ItemTypeViewLinkableEntity> = {
        getData: async () => {
            const items = Object.keys(ItemType)
                .filter((x) => isNaN(Number(x)))
                .map(
                    (x) =>
                        ({
                            name: trans.enums.itemType[x],
                            id: x,
                            value: ItemType[x] as ItemType,
                            appearsIn: [],
                        }) as ItemTypeViewLinkableEntity
                );

            const result = {
                items: items,
                hasPrev: false,
                hasNext: false,
                limit: 0,
                offset: 0,
            };
            return result;
        },
    };

    constructor() {
        super("items");

        this.productionContext = ProductionContext.getInstance();

        this.catalogDao = Factory.getInstance<CatalogDao>(CatalogDao);
        this.operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
        this.$head = $('<div class="head"><div class="logo">tastier</div></div>').appendTo(this.$view);
        new UserMenu().appendTo(this.$head);

        const scrollable = $('<div class="scrollable"></div>').appendTo(this.$view);

        this.$filterHeader = $('<div class="filter-header" style="min-height: 100px; margin: 16px;"></div>').appendTo(
            scrollable
        );
        const firstRow = $('<div class="row"></div>').appendTo(this.$filterHeader);

        this.offerSelect = new SelectBootstrap<IOffer>(
            undefined,
            Factory.getInstance<CatalogDao>(CatalogDao).offerDataSource,
            (c) => c.name,
            false,
            trans.general.all,
            undefined,
            true
        ).renderTo($('<div class="col-md"><label>' + trans.items.offer + "</label></div>").appendTo(firstRow));

        this.menuCategorySelect = new SelectBootstrap<IOfferCategory>(
            undefined,
            this.menuCategoriesDataSource,
            (c) => Format.translate(c.name, OperatorClient.context.settings!.location!.language),
            false,
            trans.general.all,
            undefined,
            true
        ).renderTo($('<div class="col-md"><label>' + trans.items.offerCategory + "</label></div>").appendTo(firstRow));

        this.itemTypeSelect = new SelectBootstrap<ItemTypeViewLinkableEntity>(
            undefined,
            this.itemTypeDataSource,
            (c) => c.name,
            false,
            trans.general.all,
            undefined,
            false
        ).renderTo($('<div class="col-md"><label>' + trans.items.itemType + "</label></div>").appendTo(firstRow));

        this.availabilitySelect = new SelectBootstrap<ItemAvailabilityViewLinkableEntity>(
            undefined,
            this.availabilityDataSource,
            (c) => c.name,
            false,
            trans.general.all,
            undefined,
            false
        ).renderTo($('<div class="col-md"><label>' + trans.items.availability + "</label></div>").appendTo(firstRow));

        const secondRow = $('<div class="row" style="margin-top: 16px;"></div>').appendTo(this.$filterHeader);

        this.$articleSearch = $(
            '<input type="text" name="name" class="form-control col-md" placeholder="' + trans.items.searchTerm + '"/>'
        ).appendTo($('<div class="col-md-3"></div>').appendTo(secondRow));

        this.$searchButton = $('<button class="btn btn-success">' + trans.items.search + "</button>").appendTo(
            $('<div class="col-md"></div>').appendTo(secondRow)
        );
        this.$searchButton.bind("click.b", () => {
            this.searchedArticleName = this.$articleSearch.val();
            this.reloadData();
        });

        this.$articleSearch.keypress((event) => {
            const keycode = event.keyCode ? event.keyCode : event.which;
            if (keycode == 13) {
                // the enter key code
                this.searchedArticleName = this.$articleSearch.val();
                this.reloadData();
                return false;
            }
        });

        $('<div id="items-table"></div>').appendTo(scrollable);

        const equals = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

        this.offerSelect.onChange = (value) => {
            if (typeof value === "object" && !equals(this.selectedOfferIds, value)) {
                this.selectedOfferIds = value;
                this.selectedMenuCategoriesIds = [];
                this.menuCategorySelect.setValues(this.selectedMenuCategoriesIds);
                this.menuCategorySelect.fetchData(() => {});
                this.reloadData();
            }
        };

        this.menuCategorySelect.onChange = (value) => {
            if (typeof value === "object") {
                if (!equals(this.selectedMenuCategoriesIds, value)) {
                    this.selectedMenuCategoriesIds = value;
                    this.reloadData();
                }
            }
        };

        this.availabilitySelect.onChange = (value) => {
            if (typeof value === "string" && value !== this.selectedAvailability) {
                this.selectedAvailability = value;
                this.reloadData();
            }
        };

        this.itemTypeSelect.onChange = (value) => {
            if (typeof value === "string" && value !== this.selectedItemType) {
                this.selectedItemType = value;
                this.reloadData();
            }
        };
    }

    public showView(transition?: ViewTransition) {
        if (!this.isVisible()) {
            this.show(transition);
        }

        this.initializeTableIfNeeded();
    }

    private initializeTableIfNeeded() {
        if (this.table !== undefined) {
            this.reloadData();
            return;
        }

        this.table = new TabulatorFull("#items-table", {
            maxHeight: "100%",
            height: "calc(100% - 164px)",
            data: this.data,
            layout: "fitColumns",
            renderHorizontal: "virtual",
            columns: [
                {
                    title: trans.items.designation,
                    field: "designation",
                    resizable: "header",
                    widthGrow: 2,
                    sorter: "string",
                    headerSort: false,
                },
                {
                    title: trans.items.standardPrice,
                    field: "standardPrice",
                    resizable: "header",
                    sorter: "string",
                    widthGrow: 1,
                    headerSort: false,
                },
                {
                    title: trans.items.available,
                    field: "isAvailable",
                    resizable: "header",
                    headerSort: false,
                    widthGrow: 1,
                    hozAlign: "center",
                    formatter: function (cell) {
                        const container = $('<div style="display: flex; justify-content: center;" </div>').appendTo(
                            cell.getElement()
                        );
                        const element = $('<input data-switch="true" type="checkbox" name="isavailable" />').appendTo(
                            container
                        );
                        const toggle = new Switch(element);
                        toggle.setValue(cell.getValue());

                        if (cell.getData().locationId !== OperatorClient.context.settings!.location!.id) {
                            toggle.setDisabled(true);
                        }

                        const operatorItemAvailabilityAndStockLevelGrant =
                            OperatorClient.context.getOperatorItemAvailabilityAndStockLevelGrant();
                        if (
                            operatorItemAvailabilityAndStockLevelGrant ===
                            OperatorItemAvailabilityAndStockLevelClaimValue.CanEdit
                        ) {
                            toggle.onChange = async (selected) => {
                                const currentValue = cell.getValue();
                                if (currentValue === selected) return;

                                try {
                                    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
                                    const response = await operatorDao.setArticleAvailability(cell.getData().id, {
                                        locationId: OperatorClient.context.settings!.location!.id,
                                        available: selected,
                                    });

                                    cell.getRow().update({
                                        unavailabilityReason:
                                            response.unavailabilityReason == null
                                                ? undefined
                                                : ArticleUnavailabilityReason[response.unavailabilityReason],
                                    });
                                    cell.setValue(response.isAvailable);
                                    cell.getRow().getCells()[3].setValue(response.isAvailable);
                                } catch (ex) {
                                    logError(ex);
                                    toggle.setValue(currentValue);

                                    const err = ex as DaoError;
                                    if (
                                        err?.additionalData?.reason ==
                                        exceptionTypes.articleActivationInsufficientStockException
                                    ) {
                                        Popup.show({
                                            title: trans.general.failed,
                                            message: trans.items.insufficientStock,
                                        });
                                        return;
                                    }

                                    Popup.show({
                                        title: trans.general.failed,
                                        message: trans.general.operationFailed,
                                        uniqueId: "operation-failed-popup",
                                    });
                                }
                            };
                        } else {
                            toggle.setDisabled(true);
                        }

                        if (
                            !cell.getValue() &&
                            cell.getData().unavailabilityReason === ArticleUnavailabilityReason[1]
                        ) {
                            $(
                                '<i style="background-image: url(&quot;/assets/img/operator/add_day_dark_gray.svg&quot;); width: 24px; height: 24px; margin: auto 8px; background-size: cover;"></i>'
                            ).appendTo(container);
                        }
                        return cell.getElement().lastElementChild;
                    },
                },
                {
                    title: trans.items.notAvailableToday,
                    field: "isAvailable",
                    resizable: "header",
                    widthGrow: 1,
                    headerSort: false,
                    hozAlign: "center",
                    formatter: function (cell) {
                        if (cell.getData().locationId !== OperatorClient.context.settings!.location!.id) {
                            return null;
                        }

                        const button = $(
                            '<button class="btn btn-success temp-not-available-item-btn"><i style="background-image: url(&quot;/assets/img/operator/add_day.svg&quot;); display: inline-block; height: 23px;  width: 23px;  background-size: cover;"></i></button>'
                        ).appendTo(cell.getElement());

                        if (cell.getData().isAvailable === false) {
                            button.prop("disabled", true);
                        } else {
                            button.prop("disabled", false);
                        }

                        const operatorItemAvailabilityAndStockLevelGrant =
                            OperatorClient.context.getOperatorItemAvailabilityAndStockLevelGrant();
                        if (
                            operatorItemAvailabilityAndStockLevelGrant !==
                            OperatorItemAvailabilityAndStockLevelClaimValue.CanEdit
                        ) {
                            button.prop("disabled", true);
                        } else {
                            button.bind("click.b", async () => {
                                if (button.is(":disabled") || cell.getValue() === false) {
                                    return;
                                }

                                try {
                                    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
                                    const response = await operatorDao.setArticleTempUnavailable(cell.getData().id, {
                                        locationId: OperatorClient.context.settings!.location!.id,
                                    });

                                    cell.getRow().update({
                                        unavailabilityReason:
                                            response.unavailabilityReason == null
                                                ? undefined
                                                : ArticleUnavailabilityReason[response.unavailabilityReason],
                                    });
                                    cell.setValue(response.isAvailable);
                                    cell.getRow().getCells()[2].setValue(response.isAvailable);
                                } catch (ex) {
                                    logError(ex);
                                    Popup.show({
                                        title: trans.general.failed,
                                        message: trans.general.operationFailed,
                                        uniqueId: "operation-failed-popup",
                                    });
                                }
                            });
                        }
                        return cell.getElement().lastElementChild as any;
                    },
                },
                {
                    title: trans.items.currentStock,
                    field: "stock",
                    resizable: "header",
                    headerSort: false,
                    widthGrow: 1,
                    hozAlign: "center",
                    formatter: (cell) => {
                        const row = cell.getRow();
                        const rowData = row.getData() as TableRow;
                        if (rowData.stock == null) return;

                        const isDisabled = OperatorClient.context.getOperatorItemAvailabilityAndStockLevelGrant() !== OperatorItemAvailabilityAndStockLevelClaimValue.CanEdit || cell.getData().locationId !== OperatorClient.context.settings!.location!.id;
                        const editor = new StockTableEditor(rowData.id, rowData.isAvailable, rowData.stock, isDisabled);
                        editor.onSaved = (updatedArticle) => {
                            row.getCells()[2].setValue(updatedArticle.isAvailable);
                            row.getCells()[3].setValue(updatedArticle.isAvailable);
                        };

                        return editor.$element[0];
                    },
                },
            ],
        });

        this.table.on("tableBuilt", () => {
            this.reloadData();
        });
    }

    protected onBecomeVisible() {
        operatorEvents.productionContext.FulfillmentAdded.add(FulfillmentSoundNotifications.playSoundIfNeeded, this);
        operatorEvents.productionContext.FulfillmentChanged.add(FulfillmentSoundNotifications.playSoundIfNeeded, this);
    }

    protected onBecomeInvisible() {
        operatorEvents.productionContext.FulfillmentAdded.remove(FulfillmentSoundNotifications.playSoundIfNeeded, this);
        operatorEvents.productionContext.FulfillmentChanged.remove(
            FulfillmentSoundNotifications.playSoundIfNeeded,
            this
        );
    }

    private async reloadData() {
        try {
            const result = await this.operatorDao.getStockArticles({
                locationId: OperatorClient.context.settings!.location!.id,
                limit: 10000,
                menuCategoryIds: this.selectedMenuCategoriesIds,
                offerIds: this.selectedOfferIds,
                articleType: this.selectedItemType,
                availability: this.selectedAvailability,
                term: this.searchedArticleName,
            });
            const filteredItems = result.items;

            this.data = filteredItems
                .map((m) => {
                    const name = getNameInLocationLanguage(m, true);
                    const standardPrice = m.isOpenPriceEnabled ? trans.items.open : toMoneyWithSign(m.price, true);

                    return new TableRow(
                        m.id,
                        name ?? "",
                        standardPrice,
                        m.isAvailable,
                        (m.unavailabilityReason == null
                            ? undefined
                            : ArticleUnavailabilityReason[m.unavailabilityReason]) ?? "",
                        m.locationId,
                        m.stock
                    );
                })
                .sort((a, b) => a.designation.localeCompare(b.designation));
            this.table.setData(this.data);
        } catch (ex) {
            logError(ex);
            Popup.show({
                message: trans.items.failedLoadingItems,
            });
        }
    }
}
